import React from "react";
import { styled } from "@mui/material/styles";
import { Paper, Rating, Typography } from "@mui/material";
import { ErrorOutline, StarBorder } from "@mui/icons-material";

import useShopContext from "@hooks/useShopContext";

import { hexToRGBWithAlpha } from "./utils/colors";

const PREFIX = "AccessDeniedContainer";

const classes = {
  backgroundColor: `${PREFIX}-backgroundColor`,
  backgroundImage: `${PREFIX}-backgroundImage`,
  centerDiv: `${PREFIX}-centerDiv`,
  rating: `${PREFIX}-rating`,
  text: `${PREFIX}-text`,
  logo: `${PREFIX}-logo`,
  paper: `${PREFIX}-paper`,
};

const Root = styled("div")(({ theme, textcolor, backgroundImage, backgroundOpacity }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  fontDisplay: "swap",
  justifyContent: "center",
  alignItems: "center",
  gap: "24px",
  padding: theme.spacing(3, 0),

  [`&.${classes.backgroundColor}`]: {
    backgroundColor: theme.palette.primary.main,
  },

  [`&.${classes.backgroundImage}`]: {
    backgroundImage: `url(${backgroundImage})`,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },

  [`& .${classes.centerDiv}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: hexToRGBWithAlpha(theme.palette.primary.main, backgroundOpacity),
    gap: theme.spacing(2),
    textAlign: "center",
    maxWidth: "550px",
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down("md")]: {
      width: "85%",
    },
  },

  [`& .${classes.rating}`]: {
    color: theme.palette.secondary.main,
  },

  [`& .${classes.text}`]: {
    color: textcolor || theme.palette.primary.contrastText,
  },

  [`& .${classes.logo}`]: {
    maxWidth: "135px",
    [theme.breakpoints.down("md")]: {
      maxWidth: "30%",
    },
  },

  [`& .${classes.paper}`]: {
    display: "flex",
    padding: theme.spacing(2),
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    backgroundColor: "#ffe3e8",
  },
}));

const AccessDeniedContainer = () => {
  const { shop } = useShopContext();

  const haveBackgroundImage = shop && shop.background_image;

  return (
    <Root
      className={`${haveBackgroundImage ? classes.backgroundImage : classes.backgroundColor}`}
      textcolor={shop?.text_color}
      backgroundImage={shop?.background_image}
      backgroundOpacity={shop?.text_background_opacity}
    >
      {(shop?.logo || shop?.vendor?.company_photo) && (
        <img
          src={shop?.logo || shop?.vendor?.company_photo}
          alt="shop logo"
          className={classes.logo}
        />
      )}
      <div className={classes.centerDiv}>
        <Typography variant="h4" className={classes.text}>
          {shop?.title}
        </Typography>
        {shop && shop.display_reviews && (
          <div
            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}
          >
            <Rating
              value={shop && shop.shop_testimonial_info.shop_rating}
              precision={0.1}
              className={classes.rating}
              readOnly
              emptyIcon={<StarBorder fontSize="inherit" className={classes.rating} />}
            />
            <Typography variant="body1" className={classes.text} style={{ fontWeight: 500 }}>
              based on {shop && shop.shop_testimonial_info.shop_testimonials_length} review(s)
            </Typography>
          </div>
        )}
        <Paper className={classes.paper} elevation={2}>
          <ErrorOutline />
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            This shop is currently not available in your country.
          </Typography>
        </Paper>
      </div>
    </Root>
  );
};

export default AccessDeniedContainer;
